
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../providers/AuthProvider';
import api from '../../lib/api';
import { Task, TaskManagerProps } from '../../types/task';
import type { Project } from '../../types/project';
import TaskList from './TaskList';
import { Form, Input, Select, Button, Space, Alert, Spin, Empty, Typography, Card, Divider, Row, Col } from 'antd';
import { PlusOutlined, EditOutlined, SaveOutlined, CloseOutlined } from '@ant-design/icons';
import { usePomodoro } from '../../context/PomodoroContext';
import eventBus, { EVENTS } from '../../utils/eventBus';

export default function TaskManager({ onSelectTask }: TaskManagerProps) {
  const { isAuthenticated, user } = useAuth();
  const { state } = usePomodoro();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // 新任务表单状态
  const [newTaskCategory, setNewTaskCategory] = useState('');
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [showCategoryInput, setShowCategoryInput] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);

  // 编辑任务状态
  const [editingTaskId, setEditingTaskId] = useState<number | null>(null);
  const [editTaskCategory, setEditTaskCategory] = useState('');
  const [editTaskTitle, setEditTaskTitle] = useState('');
  const [showEditCategoryInput, setShowEditCategoryInput] = useState(false);
  
  // 获取任务列表
  const fetchTasks = async () => {
    try {
      setLoading(true);
      const [todoTasks, completedTasks] = await Promise.all([
        api.tasks.getTodoList(),
        api.tasks.getCompletedToday()
      ]);
      const data = [...todoTasks, ...completedTasks];
      setTasks(data);
      
      // 提取所有唯一的类别
      const uniqueCategories = Array.from(new Set(
        data
          .map(task => task.category)
          .filter((category): category is string => category !== null && category !== '')
      )) as string[];
      setCategories(uniqueCategories);
      
      // 自动选择第一条未完成的任务数据用于番茄钟
      if (data.length > 0) {
        // 优先选择未完成的任务
        const firstPendingTask = todoTasks[0]; // Since todoTasks contains only pending tasks
        if (firstPendingTask) {
          onSelectTask(firstPendingTask.id, firstPendingTask.title);
        } else {
          // 如果没有未完成的任务，选择第一条任务
          onSelectTask(data[0].id, data[0].title);
        }
      }
    } catch (err) {
      setError('无法加载任务列表');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  
  // 初始加载任务
  useEffect(() => {
    if (isAuthenticated) {
      fetchTasks();
    }
  }, [isAuthenticated]);

  // 拉取项目列表（用于新建任务时选择项目）
  useEffect(() => {
    if (isAuthenticated) {
      api.projects
        .list()
        .then((data: Project[]) => setProjects(data.filter((p) => !p.archived)))
        .catch(() => setProjects([]));
    }
  }, [isAuthenticated]);

  // 监听任务完成事件
  useEffect(() => {
    const handleTaskCompleted = (updatedTask: Task) => {
      // 更新本地状态
      setTasks(prevTasks => prevTasks.map(task => 
        task.id === updatedTask.id ? updatedTask : task
      ));
      
      // 如果被完成的任务是当前的番茄钟任务，找出下一个未完成的任务
      if (state.currentTaskId === updatedTask.id) {
        const nextPendingTask = tasks.find(t => !t.completed && t.id !== updatedTask.id);
        if (nextPendingTask) {
          onSelectTask(nextPendingTask.id, nextPendingTask.title);
        }
      }
    };
    
    // 订阅事件
    eventBus.on(EVENTS.TASK_COMPLETED, handleTaskCompleted);
    
    // 组件卸载时取消订阅
    return () => {
      eventBus.off(EVENTS.TASK_COMPLETED, handleTaskCompleted);
    };
  }, [tasks, state.currentTaskId, onSelectTask]);
  
  // 添加新任务
  const handleAddTask = async () => {
    if (!newTaskTitle.trim()) {
      return;
    }
    
    try {
      const newTask = await api.tasks.create({
        title: newTaskTitle,
        category: newTaskCategory || undefined,
        project_id: selectedProjectId,
      });

      setTasks([newTask, ...tasks]);

      // 如果类别是新的，添加到类别列表
      if (newTaskCategory && !categories.includes(newTaskCategory)) {
        setCategories([...categories, newTaskCategory]);
      }

      // 如果当前没有选择任务用于番茄钟，自动选择新添加的任务
      if (!state.currentTaskId) {
        onSelectTask(newTask.id, newTask.title);
      }

      // 清空表单
      setNewTaskCategory('');
      setNewTaskTitle('');
      setSelectedProjectId(null);
    } catch (err) {
      setError('添加任务失败');
      console.error(err);
    }
  };
  
  // 在顶层任务里找一个任务：taskId 可能是根任务本身的 id，也可能是嵌
  // 在某个根任务 subtasks 数组里的子任务 id（tasks 状态本身只有根任务，
  // 子任务不会出现在顶层，所以不能直接用 tasks.find）。
  // 根任务优先匹配，结果与原来的 tasks.find(task => task.id === taskId)
  // 完全一致，不改变番茄钟相关的既有行为。
  const findTaskById = (taskId: number): Task | undefined => {
    for (const task of tasks) {
      if (task.id === taskId) return task;
      const subtask = task.subtasks.find(s => s.id === taskId);
      if (subtask) return subtask;
    }
    return undefined;
  };

  // 切换任务完成状态（根任务或子任务均可）
  const toggleTaskCompletion = async (taskId: number) => {
    const taskToUpdate = findTaskById(taskId);
    if (!taskToUpdate) return;

    try {
      const updatedTask = await api.tasks.update(taskId, {
        completed: !taskToUpdate.completed,
      });

      // 如果这个任务原来没有完成，但现在被标记为完成，并且它是当前的番茄钟任务
      if (!taskToUpdate.completed && updatedTask.completed && state.currentTaskId === taskId) {
        // 找出下一个未完成的任务作为番茄钟任务（番茄钟只挂在根任务上，
        // 这里维持原有的顶层查找范围不变）
        const nextPendingTask = tasks.find(t => !t.completed && t.id !== taskId);
        if (nextPendingTask) {
          onSelectTask(nextPendingTask.id, nextPendingTask.title);
        }
      }

      // 更新本地状态：命中根任务就整体替换；命中子任务就替换该根任务
      // subtasks 数组里对应的一项，不动其余结构
      setTasks(tasks.map(task => {
        if (task.id === taskId) return updatedTask;
        if (task.subtasks.some(s => s.id === taskId)) {
          return {
            ...task,
            subtasks: task.subtasks.map(s => (s.id === taskId ? updatedTask : s)),
          };
        }
        return task;
      }));
    } catch (err) {
      setError('更新任务失败');
      console.error(err);
    }
  };
  
  // 删除任务
  const deleteTask = async (taskId: number) => {
    try {
      await api.tasks.delete(taskId);
      
      // 检查被删除的任务是否是当前的番茄钟任务
      if (state.currentTaskId === taskId) {
        // 找出下一个未完成的任务作为番茄钟任务
        const nextPendingTask = tasks.find(t => !t.completed && t.id !== taskId);
        if (nextPendingTask) {
          onSelectTask(nextPendingTask.id, nextPendingTask.title);
        }
      }
      
      // 从本地状态中移除任务
      setTasks(tasks.filter(task => task.id !== taskId));
    } catch (err) {
      setError('删除任务失败');
      console.error(err);
    }
  };
  
  // 恢复已完成任务
  const restoreTask = async (taskId: number) => {
    await toggleTaskCompletion(taskId);
  };
  
  // 开始编辑任务
  const startEditingTask = (task: Task) => {
    setEditingTaskId(task.id);
    setEditTaskTitle(task.title);
    setEditTaskCategory(task.category || '');
    setShowEditCategoryInput(false);
  };
  
  // 取消编辑任务
  const cancelEditingTask = () => {
    setEditingTaskId(null);
    setEditTaskTitle('');
    setEditTaskCategory('');
    setShowEditCategoryInput(false);
  };
  
  // 保存编辑后的任务
  const saveEditedTask = async (taskId: number) => {
    if (!editTaskTitle.trim()) {
      return;
    }
    
    try {
      const updatedTask = await api.tasks.update(taskId, {
        title: editTaskTitle,
        category: editTaskCategory || undefined,
      });
      
      // 更新本地状态
      setTasks(tasks.map(task => 
        task.id === taskId ? updatedTask : task
      ));
      
      // 如果类别是新的，添加到类别列表
      if (editTaskCategory && !categories.includes(editTaskCategory)) {
        setCategories([...categories, editTaskCategory]);
      }
      
      // 重置编辑状态
      cancelEditingTask();
    } catch (err) {
      setError('更新任务失败');
      console.error(err);
    }
  };
  
  // 格式化时间
  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };
  
  // 计算统计信息
  const pendingTasks = tasks.filter(task => !task.completed);
  const completedTasks = tasks.filter(task => task.completed);
  
  // 自定义任务渲染函数
  const renderCustomTaskItem = (task: Task, index: number) => {
    // 如果这个任务是正在编辑的任务，则渲染编辑表单
    if (task.id === editingTaskId) {
      return (
        <div 
          style={{ 
            opacity: 1,
            cursor: 'default',
            marginBottom: '8px',
          }}
        >
          <Card
            size="small"
            style={{ 
              backgroundColor: task.completed ? '#f9f9f9' : 'white',
              borderLeft: index === 0 && !task.completed ? '3px solid #1890ff' : undefined
            }}
            styles={{ body: { padding: '8px 12px' } }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {/* 第一行：类别和标题在同一行 */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'nowrap', width: '100%' }}>
                {/* 类别选择器 */}
                <div style={{ width: '30%', minWidth: '100px', maxWidth: '180px' }}>
                  {showEditCategoryInput ? (
                    <Input
                      placeholder="输入类别..."
                      value={editTaskCategory}
                      onChange={(e) => setEditTaskCategory(e.target.value)}
                      onBlur={() => {
                        if (!editTaskCategory) {
                          setShowEditCategoryInput(false);
                        }
                      }}
                      autoFocus
                      size="small"
                    />
                  ) : (
                    <Select
                      style={{ width: '100%' }}
                      value={editTaskCategory}
                      onChange={(value) => {
                        if (value === '+') {
                          setShowEditCategoryInput(true);
                          setEditTaskCategory('');
                        } else {
                          setEditTaskCategory(value);
                        }
                      }}
                      placeholder="选择类别"
                      allowClear
                      size="small"
                      dropdownRender={(menu) => (
                        <>
                          {menu}
                          <Divider style={{ margin: '4px 0' }} />
                          <Button
                            type="text"
                            icon={<PlusOutlined />}
                            onClick={() => {
                              setShowEditCategoryInput(true);
                              setEditTaskCategory('');
                            }}
                            style={{ width: '100%', textAlign: 'left', padding: '4px 8px' }}
                            size="small"
                          >
                            新类别
                          </Button>
                        </>
                      )}
                    >
                      {categories.map(category => (
                        <Select.Option key={category} value={category}>{category}</Select.Option>
                      ))}
                    </Select>
                  )}
                </div>
                
                {/* 任务标题和按钮 */}
                <div style={{ flex: 1, display: 'flex', justifyContent: 'space-between', minWidth: 0 }}>
                  <Input
                    placeholder="编辑任务..."
                    value={editTaskTitle}
                    onChange={(e) => setEditTaskTitle(e.target.value)}
                    required
                    style={{ marginRight: '8px', flex: 1, minWidth: 0 }}
                    size="small"
                  />
                  <Space size={4} style={{ flexShrink: 0 }}>
                    <Button 
                      type="primary" 
                      size="small"
                      icon={<SaveOutlined />} 
                      onClick={() => saveEditedTask(task.id)}
                    >
                      保存
                    </Button>
                    <Button 
                      size="small"
                      icon={<CloseOutlined />} 
                      onClick={cancelEditingTask}
                    >
                      取消
                    </Button>
                  </Space>
                </div>
              </div>
              
              {/* 第二行显示创建时间 */}
              <div>
                <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                  {formatTime(task.created_at)}
                </Typography.Text>
              </div>
            </div>
          </Card>
        </div>
      );
    }
    
    // 否则返回null，使用默认渲染
    return null;
  };
  
  // 如果用户未登录，显示登录提示
  if (!isAuthenticated) {
    return (
      <Card variant="borderless" className="w-full">
        <Typography.Text type="secondary">请登录以管理您的任务</Typography.Text>
      </Card>
    );
  }
  
  return (
    <Card variant="borderless" className="w-full shadow-lg">
      <Card.Meta
        title={<Typography.Title level={4} style={{ margin: 0 }}>任务列表</Typography.Title>}
        description={
          <Typography.Text type="secondary">
            {pendingTasks.length} 待办, {completedTasks.length} 已完成
          </Typography.Text>
        }
      />
      
      <Divider style={{ margin: '16px 0' }} />
      
      {/* 添加任务表单 */}
      <Form onFinish={handleAddTask} layout="vertical" className="mb-4">
        <Row gutter={[8, 8]} wrap={false} align="middle">
          <Col style={{ width: '30%', minWidth: '100px', maxWidth: '180px' }}>
            <Form.Item className="mb-0" style={{ width: '100%' }}>
              {showCategoryInput ? (
                <Input
                  placeholder="输入类别..."
                  value={newTaskCategory}
                  onChange={(e) => setNewTaskCategory(e.target.value)}
                  onBlur={() => {
                    if (!newTaskCategory) {
                      setShowCategoryInput(false);
                    }
                  }}
                  autoFocus
                  size="small"
                />
              ) : (
                <Select
                  style={{ width: '100%' }}
                  value={newTaskCategory}
                  onChange={(value) => {
                    if (value === '+') {
                      setShowCategoryInput(true);
                      setNewTaskCategory('');
                    } else {
                      setNewTaskCategory(value);
                    }
                  }}
                  placeholder="选择类别"
                  allowClear
                  size="small"
                  dropdownRender={(menu) => (
                    <>
                      {menu}
                      <Divider style={{ margin: '4px 0' }} />
                      <Button
                        type="text"
                        icon={<PlusOutlined />}
                        onClick={() => {
                          setShowCategoryInput(true);
                          setNewTaskCategory('');
                        }}
                        style={{ width: '100%', textAlign: 'left', padding: '4px 8px' }}
                        size="small"
                      >
                        新类别
                      </Button>
                    </>
                  )}
                >
                  {categories.map(category => (
                    <Select.Option key={category} value={category}>{category}</Select.Option>
                  ))}
                </Select>
              )}
            </Form.Item>
          </Col>
          <Col style={{ width: 160, flexShrink: 0 }}>
            <Form.Item className="mb-0" style={{ width: '100%' }}>
              <Select
                allowClear
                placeholder="项目（可不选）"
                style={{ width: 160 }}
                size="small"
                value={selectedProjectId ?? undefined}
                onChange={(value) => setSelectedProjectId(value ?? null)}
                options={projects.map((p) => ({ value: p.id, label: p.name }))}
              />
            </Form.Item>
          </Col>
          <Col flex="1" style={{ minWidth: 0 }}>
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item className="flex-1 mb-0" style={{ width: 'calc(100% - 88px)', minWidth: 0 }}>
                <Input
                  placeholder="添加新任务..."
                  value={newTaskTitle}
                  onChange={(e) => setNewTaskTitle(e.target.value)}
                  required
                  size="small"
                />
              </Form.Item>
              <Form.Item className="mb-0">
                <Button type="primary" htmlType="submit" style={{ width: 88 }} size="small">
                  添加
                </Button>
              </Form.Item>
            </Space.Compact>
          </Col>
        </Row>
      </Form>
      
      {/* 错误提示 */}
      {error && (
        <Alert
          message={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          className="mb-4"
        />
      )}
      
      {/* 任务列表 */}
      {loading ? (
        <div className="text-center py-8">
          <Spin>
            <div style={{ padding: '30px', textAlign: 'center' }}>
              <Typography.Text type="secondary">加载中...</Typography.Text>
            </div>
          </Spin>
        </div>
      ) : tasks.length === 0 ? (
        <Empty description="暂无任务，添加一个吧!" className="py-8" />
      ) : (
        <div className="task-list-container">
          <TaskList 
            tasks={tasks}
            onUpdateTasks={setTasks}
            onToggleComplete={toggleTaskCompletion}
            onDelete={deleteTask}
            onRestore={restoreTask}
            onEdit={startEditingTask}
            onSelectTask={onSelectTask}
            renderCustomItem={(task, index) => 
              task.id === editingTaskId ? renderCustomTaskItem(task, index) : null
            }
          />
        </div>
      )}
    </Card>
  );
} 